import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MapPin, Phone, Search, SearchX, ShoppingBag, Utensils, X } from 'lucide-react';
import { getMerchantProfile } from '../../services/merchant';
import { GetMenuItemsByMerchant } from '../../hooks/reactQuery';
import { formatCurrency } from '../../Utils';
import ItemCustomization from '../../components/productDetails/ItemCustomization';
import CartContext from '../../Context/Cart/CartContext';
import PublicMenuOrderPanel from '../../components/PublicMenu/PublicMenuOrderPanel';

const itemKey = (item) => item.item_public_id || item.item_id || item.id || item.name;

function PublicMenuRow({ item, accentColor, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="flex w-full gap-3 rounded-2xl border border-neutral-200 bg-white p-3 text-left shadow-sm shadow-neutral-900/[0.03] transition active:scale-[0.99]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-[15px] font-semibold leading-5 text-neutral-950">{item.name}</h3>
          <span className="shrink-0 text-[14px] font-semibold text-neutral-950">{formatCurrency(item.price)}</span>
        </div>
        {item.description && <p className="mt-1.5 line-clamp-2 text-[13px] leading-5 text-neutral-500">{item.description}</p>}
        <span className="mt-3 inline-flex h-8 items-center rounded-full border border-neutral-200 bg-[#f7f7f5] px-3 text-[12px] font-semibold" style={{ color: accentColor }}>
          Add item
        </span>
      </div>
      <div className="h-[88px] w-[88px] shrink-0 overflow-hidden rounded-xl bg-neutral-100">
        {item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" />}
      </div>
    </button>
  );
}

export default function PublicMenu() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tableNumber = searchParams.get('table') || 'Online';
  const [merchant, setMerchant] = useState(null);
  const [loadingMerchant, setLoadingMerchant] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const { cartItems, itemCount, total } = useContext(CartContext);

  useEffect(() => {
    const fetchMerchant = async () => {
      try {
        const res = await getMerchantProfile(slug);
        const restaurant = res?.data?.restaurant || res?.data || null;
        setMerchant(restaurant);
      } catch (error) {
        setMerchant(null);
      } finally {
        setLoadingMerchant(false);
      }
    };
    if (slug) fetchMerchant();
  }, [slug]);

  const merchantId = merchant?.id;
  const { data: menuCategories, isLoading: loadingMenu } = GetMenuItemsByMerchant(merchantId);
  const accentColor = merchant?.brand_colors?.primary || merchant?.primary_color || '#1A7F6D';
  const isLoading = loadingMerchant || (!!merchantId && loadingMenu);
  const restaurantName = merchant?.name || merchant?.restaurant_name || 'Restaurant';
  const tableLabel = tableNumber === 'Online' ? 'Online order' : `Table ${tableNumber}`;
  const logoImage = merchant?.logo_url || merchant?.logo || merchant?.image_url || '';
  const coverImage = merchant?.cover_url || merchant?.cover_image_url || '';
  const address = merchant?.address || merchant?.location || merchant?.location_description;
  const phone = merchant?.phone || merchant?.contact_phone;
  const categories = useMemo(() => (
    Array.isArray(menuCategories) ? menuCategories : []
  ), [menuCategories]);
  const allItems = useMemo(() => (
    categories.flatMap((category) => {
      const categoryName = category.name || category.category_name || 'Menu';
      return (category.menu_items || category.items || []).map((item) => ({
        ...item,
        id: item.item_id || item.id,
        name: item.item_name || item.name,
        description: item.description || '',
        price: Number(item.price || 0),
        image: item.image_url || item.image || '',
        item_name: item.item_name || item.name,
        item_public_id: item.item_public_id || item.item_id || item.id || `${categoryName}-${item.item_name || item.name}`,
        category: categoryName,
      }));
    })
  ), [categories]);

  const visibleItems = allItems.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || (item.name || '').toLowerCase().includes(query) || (item.description || '').toLowerCase().includes(query);
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const sections = (activeCategory === 'all'
    ? categories.map((category) => category.name || category.category_name).filter(Boolean)
    : [activeCategory]
  ).map((name) => ({
    name,
    items: visibleItems.filter((item) => item.category === name),
  })).filter((section) => section.items.length);

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#f7f7f5] px-6">
        <div className="flex -translate-y-4 flex-col items-center text-center" role="status" aria-live="polite">
          <div className="relative flex h-16 w-16 items-center justify-center">
            <span className="absolute inset-0 animate-spin rounded-full border-2 border-neutral-200 border-t-[#1A7F6D]" />
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[15px] font-bold text-neutral-900 shadow-sm">
              S
            </span>
          </div>
          <p className="mt-5 text-[16px] font-semibold text-neutral-950">Opening the menu</p>
          <p className="mt-1 text-[13px] text-neutral-500">
            {tableNumber === 'Online' ? 'Getting everything ready' : `Getting Table ${tableNumber} ready`}
          </p>
          <span className="sr-only">Loading menu</span>
        </div>
      </div>
    );
  }

  if (!merchant) {
    return <div className="min-h-screen bg-[#f7f7f5] px-5 pt-16 text-[14px] text-neutral-500">This menu could not be found.</div>;
  }

  return (
    <div className="min-h-screen bg-[#f7f7f5] pb-28 text-neutral-950">
      <header className="px-4 pt-4 sm:px-5 sm:pt-5">
        <div className="relative mx-auto flex min-h-[310px] max-w-3xl items-end overflow-hidden rounded-2xl bg-neutral-900 sm:min-h-[340px]">
          {coverImage && <img src={coverImage} alt={`${restaurantName} cover`} className="absolute inset-0 h-full w-full object-cover brightness-[0.48] saturate-[0.9]" />}
          <div className="absolute inset-0 bg-black/35" aria-hidden="true" />

          <div className="relative z-10 w-full px-5 pb-5 pt-24 text-white sm:px-6 sm:pb-6">
            <div className="flex items-end gap-3.5 sm:gap-4">
              <div className="flex h-[68px] w-[68px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/40 bg-white sm:h-20 sm:w-20">
                {logoImage ? (
                  <img src={logoImage} alt={`${restaurantName} logo`} className="h-full w-full object-contain p-1" />
                ) : (
                  <span className="text-[25px] font-bold" style={{ color: accentColor }}>{restaurantName.charAt(0)}</span>
                )}
              </div>
              <div className="min-w-0 flex-1 pb-0.5">
                <h1 className="text-[25px] font-bold leading-[1.12] text-white drop-shadow-md sm:text-[30px]">{restaurantName}</h1>
                {merchant.description && (
                  <p className="mt-1.5 line-clamp-2 max-w-2xl text-[13px] font-medium leading-5 text-white drop-shadow-sm sm:text-[14px]">{merchant.description}</p>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3.5 text-[13px] font-bold shadow-sm"
                style={{ backgroundColor: '#ffffff', color: '#171717' }}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accentColor }} />
                {tableLabel}
              </span>
              <span
                className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-bold shadow-sm"
                style={{ backgroundColor: '#ffffff', color: '#262626' }}
              >
                <ShoppingBag className="h-3.5 w-3.5" />
                {allItems.length} items
              </span>
            </div>

            {(address || phone) && (
              <div className="mt-4 flex flex-col gap-2 text-[13px] font-medium text-white sm:flex-row sm:flex-wrap sm:gap-x-6">
                {address && (
                  <span className="flex min-w-0 items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-white" />
                    <span className="line-clamp-2 leading-5 drop-shadow-sm">{address}</span>
                  </span>
                )}
                {phone && (
                  <a href={`tel:${String(phone).replace(/\s/g, '')}`} className="flex items-center gap-2 text-white hover:text-white">
                    <Phone className="h-4 w-4 shrink-0 text-white" />
                    <span className="drop-shadow-sm">{phone}</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="sticky top-0 z-30 mt-4 border-y border-neutral-200 bg-[#f7f7f5]/95 px-5 py-3 backdrop-blur">
        <div className="mx-auto max-w-3xl">
          <div className="flex h-11 items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3">
            <Search className="h-4 w-4 text-neutral-400" />
            <input
              value={searchQuery}
              onFocus={() => setActiveCategory('all')}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                if (event.target.value) setActiveCategory('all');
              }}
              placeholder="Search dishes"
              className="h-full min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-neutral-400"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} aria-label="Clear search">
                <X className="h-4 w-4 text-neutral-400" />
              </button>
            )}
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            <button
              type="button"
              onClick={() => setActiveCategory('all')}
              className="h-9 shrink-0 rounded-full px-4 text-[13px] font-semibold"
              style={activeCategory === 'all' ? { backgroundColor: accentColor, color: '#fff' } : { backgroundColor: '#fff', color: '#525252', border: '1px solid #e5e5e5' }}
            >
              All
            </button>
            {categories.map((category) => {
              const categoryName = category.name || category.category_name;
              return (
                <button
                  key={categoryName}
                  type="button"
                  onClick={() => setActiveCategory(categoryName)}
                  className="h-9 shrink-0 rounded-full px-4 text-[13px] font-semibold"
                  style={activeCategory === categoryName ? { backgroundColor: accentColor, color: '#fff' } : { backgroundColor: '#fff', color: '#525252', border: '1px solid #e5e5e5' }}
                >
                  {categoryName}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-5">
        {sections.length === 0 && (
          <div className="flex min-h-[300px] flex-col items-center justify-center px-6 py-14 text-center">
            {searchQuery.trim() ? (
              <>
                <SearchX className="h-8 w-8 text-neutral-300" />
                <h2 className="mt-4 text-[17px] font-semibold text-neutral-900">No dishes found</h2>
                <p className="mt-1 max-w-[290px] text-[13px] leading-5 text-neutral-500">
                  We could not find anything matching “{searchQuery.trim()}”. Try another name or browse the full menu.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('all');
                  }}
                  className="mt-5 h-10 rounded-xl border border-neutral-300 bg-white px-4 text-[13px] font-semibold text-neutral-800"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <Utensils className="h-8 w-8 text-neutral-300" />
                <h2 className="mt-4 text-[17px] font-semibold text-neutral-900">Nothing available here yet</h2>
                <p className="mt-1 max-w-[280px] text-[13px] leading-5 text-neutral-500">This restaurant is updating this part of the menu. Please check another category.</p>
                {activeCategory !== 'all' && (
                  <button type="button" onClick={() => setActiveCategory('all')} className="mt-5 h-10 rounded-xl border border-neutral-300 bg-white px-4 text-[13px] font-semibold text-neutral-800">
                    View full menu
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {sections.map((section) => (
          <section key={section.name} className="pt-6">
            <div className="mb-1 flex items-end justify-between">
              <h2 className="text-[18px] font-semibold tracking-tight">{section.name}</h2>
              <span className="text-[12px] text-neutral-400">{section.items.length} item{section.items.length === 1 ? '' : 's'}</span>
            </div>
            <div className="space-y-3">
              {section.items.map((item) => (
                <PublicMenuRow key={itemKey(item)} item={item} accentColor={accentColor} onSelect={setSelectedItem} />
              ))}
            </div>
          </section>
        ))}
      </main>

      {cartItems.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 px-5 py-3 backdrop-blur">
          <button
            type="button"
            onClick={() => setShowCart(true)}
            className="mx-auto flex h-14 w-full max-w-3xl items-center justify-between rounded-xl px-4 text-[14px] font-semibold text-white"
            style={{ backgroundColor: accentColor }}
          >
            <span>{itemCount} item{itemCount === 1 ? '' : 's'} in cart</span>
            <span>{formatCurrency(Number(total || 0))}</span>
          </button>
        </div>
      )}

      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 px-0 pt-8 sm:items-center sm:px-4">
          <div className="h-[92vh] w-full overflow-hidden rounded-t-[28px] bg-[#f7f7f5] shadow-2xl sm:max-w-xl sm:rounded-[28px]">
            <ItemCustomization
              item={selectedItem}
              onClose={() => setSelectedItem(null)}
            />
          </div>
        </div>
      )}

      {showCart && (
        <PublicMenuOrderPanel
          open={showCart}
          onClose={() => setShowCart(false)}
          tableNumber={tableNumber}
          merchantId={merchant.id}
          merchantName={merchant.name}
          accentColor={accentColor}
        />
      )}

    </div>
  );
}
